import React from 'react';
import { rgba } from '../../theme/PrintTheme';
import type { PrintTemplate as PrintDesign } from '../../theme/PrintTemplate';

// A thumbnail of the page design, drawn small on each card. Deliberately crude
// — grey bars stand in for the church name and the body copy, so the eye
// compares the *layout*, not the words. The shapes are read from the same
// design record that generates the printable CSS, so a card cannot show a band
// the template does not have.

const bar = (w: string, top: string, light: boolean, left = '10px') => (
  <div
    key={`${w}${top}${left}`}
    style={{
      position: 'absolute', left, top, width: w, height: '4px', borderRadius: '2px',
      backgroundColor: light ? 'rgba(255,255,255,0.9)' : '#BBB'
    }}
  />
);

const headerShape = (d: PrintDesign, accent: string) => {
  switch (d.header) {
    case 'band':
      return <div style={{ position: 'absolute', inset: '0 0 auto 0', height: '38px', backgroundColor: accent }} />;
    case 'diagonal':
      return (
        <div style={{
          position: 'absolute', inset: '0 0 auto 0', height: '40px',
          background: `linear-gradient(112deg, ${accent} 0%, ${accent} 58%, ${rgba(accent, 0.65)} 58%)`,
          clipPath: 'polygon(0 0, 100% 0, 100% 76%, 0 100%)'
        }} />
      );
    case 'wave':
      return (
        <div style={{
          position: 'absolute', inset: '0 0 auto 0', height: '40px', backgroundColor: accent,
          borderBottomLeftRadius: '58% 14px', borderBottomRightRadius: '58% 14px'
        }} />
      );
    case 'split':
      return (
        <div style={{
          position: 'absolute', inset: '0 0 auto 0', height: '38px',
          background: `linear-gradient(90deg, ${accent} 0%, ${accent} 58%, ${rgba(accent, 0.18)} 58%)`
        }} />
      );
    case 'sidebar':
      return <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '26px', backgroundColor: accent }} />;
    case 'outline':
      return <div style={{ position: 'absolute', left: '8px', right: '8px', top: '6px', height: '32px', border: `2px solid ${accent}` }} />;
    case 'stacked':
      return (
        <>
          <div style={{ position: 'absolute', left: '8px', right: '8px', top: '8px', height: '3px', backgroundColor: accent }} />
          <div style={{ position: 'absolute', left: '8px', right: '8px', top: '36px', height: '3px', backgroundColor: accent }} />
        </>
      );
    default:
      return <div style={{ position: 'absolute', left: '10px', right: '10px', top: '36px', height: '1px', backgroundColor: '#333' }} />;
  }
};

const decorationShape = (d: PrintDesign, accent: string) => {
  switch (d.decoration) {
    case 'corner-triangle':
      return (
        <div style={{
          position: 'absolute', bottom: 0, right: 0, width: '34px', height: '20px',
          backgroundColor: rgba(accent, 0.2), clipPath: 'polygon(100% 0, 100% 100%, 0 100%)'
        }} />
      );
    case 'corner-arcs':
      return (
        <div style={{
          position: 'absolute', top: '-14px', right: '-14px', width: '40px', height: '40px',
          borderRadius: '50%', backgroundColor: rgba(accent, 0.16)
        }} />
      );
    case 'side-accent':
      return <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', backgroundColor: accent }} />;
    case 'gradient-bar':
      return <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', backgroundColor: accent }} />;
    default:
      return null;
  }
};

export interface LayoutThumbnailProps {
  design: PrintDesign;
  /** The parish's own brand colour, so the card shows the real ink. */
  accent: string;
  height?: string;
  border?: string;
}

const LayoutThumbnail = ({ design, accent, height = '72px', border = '#DDD' }: LayoutThumbnailProps) => {
  const onBand = ['band', 'diagonal', 'wave', 'split'].includes(design.header);

  const identity = design.header === 'sidebar'
    ? [bar('16px', '12px', true, '5px'), bar('12px', '21px', true, '5px')]
    : [bar('50%', '12px', onBand), bar('30%', '21px', onBand)];

  const frame = design.border === 'none' ? null : (
    <div style={{
      position: 'absolute', inset: '3px',
      border: design.border === 'double' ? `3px double ${accent}` : `1px solid ${accent}`,
      outline: design.border === 'ornamental' ? `1px solid ${rgba(accent, 0.4)}` : undefined,
      outlineOffset: '2px'
    }} />
  );

  const footer = design.footer === 'band'
    ? <div style={{ position: 'absolute', inset: 'auto 0 0 0', height: '10px', backgroundColor: accent }} />
    : design.footer === 'double-rule'
      ? <div style={{ position: 'absolute', left: '10px', right: '10px', bottom: '8px', height: '3px', borderTop: `3px double ${accent}` }} />
      : null;

  return (
    <div style={{
      position: 'relative', height, borderRadius: '6px', overflow: 'hidden',
      backgroundColor: '#FFF', border: `1px solid ${border}`
    }}>
      {headerShape(design, accent)}
      {decorationShape(design, accent)}
      {identity}
      {bar('72%', '48px', false)}
      {bar('60%', '57px', false)}
      {footer}
      {frame}
    </div>
  );
};

/** What the chosen design does, in plain words — the chips under the grid. */
export const layoutAttributes = (d: PrintDesign): Array<[string, string]> => [
  ['Header', d.header],
  ['Logo', d.logo],
  ['Title', d.title],
  ['Table', d.table],
  ['Footer', d.footer],
  ['Border', d.border],
  ['Decoration', d.decoration],
  ['Watermark', d.watermark ? 'yes' : 'no']
];

export default LayoutThumbnail;
